<template>
  <div class="p-6">
    <!-- Header -->
    <div class="mb-6 flex items-center justify-between">
      <div>
        <h1 class="text-3xl font-bold" style="color: var(--text-primary)">💰 {{ t('financeiro.title') }}</h1>
        <p class="text-sm mt-1" style="color: var(--text-secondary)">{{ t('financeiro.subtitle') }}</p>
      </div>
      <div class="flex gap-3">
        <button 
          @click="currentView = 'dashboard'" 
          :class="['btn-menu', currentView === 'dashboard' ? 'btn-menu-active' : 'btn-menu-inactive']">
          📊 {{ t('financeiro.nav.dashboard') }}
        </button>
        <button 
          @click="currentView = 'clientes'" 
          :class="['btn-menu', currentView === 'clientes' ? 'btn-menu-active' : 'btn-menu-inactive']">
          👥 {{ t('financeiro.nav.clientes') }}
        </button>
        <button 
          @click="currentView = 'cobrancas'" 
          :class="['btn-menu', currentView === 'cobrancas' ? 'btn-menu-active' : 'btn-menu-inactive']">
          💵 {{ t('financeiro.nav.cobrancas') }}
        </button>
        <button 
          @click="currentView = 'contas-pagar'" 
          :class="['btn-menu', currentView === 'contas-pagar' ? 'btn-menu-active' : 'btn-menu-inactive']">
          💳 {{ t('financeiro.nav.pagamentos') }}
        </button>
        <button 
          @click="currentView = 'relatorios'" 
          :class="['btn-menu', currentView === 'relatorios' ? 'btn-menu-active' : 'btn-menu-inactive']">
          📈 {{ t('financeiro.nav.relatorios') }}
        </button>
        <button 
          @click="currentView = 'configuracoes'" 
          :class="['btn-menu', currentView === 'configuracoes' ? 'btn-menu-active' : 'btn-menu-inactive']">
          ⚙️ {{ t('financeiro.nav.configuracoes') }}
        </button>
      </div>
    </div>

    <!-- Dashboard View -->
    <div v-if="currentView === 'dashboard'">
      <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <!-- Card Mês Atual -->
        <div class="card dashboard-card dashboard-card-primary">
          <div class="dashboard-card-header">
            <div class="dashboard-card-title">
              <span class="dashboard-icon">📅</span>
              <div>
                <h3 class="text-lg font-semibold" style="color: var(--text-primary)">{{ t('financeiro.dashboard.currentMonth') }}</h3>
                <p class="text-xs" style="color: var(--text-secondary)">{{ t('financeiro.dashboard.financialSummary') }}</p>
              </div>
            </div>
          </div>
          <div class="dashboard-card-body">
            <div class="dashboard-row">
              <span style="color: var(--text-secondary)">{{ t('financeiro.dashboard.paid') }}</span>
              <MoneyAmount :value="dashboard.mesAtual?.totalPago || 0" class="font-bold text-green-600" />
            </div>
            <div class="dashboard-row">
              <span style="color: var(--text-secondary)">{{ t('financeiro.dashboard.pending') }}</span>
              <MoneyAmount :value="dashboard.mesAtual?.totalPendente || 0" class="font-bold text-orange-600" />
            </div>
            <div class="dashboard-total">
              <span style="color: var(--text-primary)" class="font-semibold">{{ t('financeiro.dashboard.total') }}</span>
              <MoneyAmount :value="dashboard.mesAtual?.total || 0" class="dashboard-total-value" />
            </div>
          </div>
        </div>

        <!-- Card Previsão Próximo Mês -->
        <div class="card dashboard-card dashboard-card-secondary">
          <div class="dashboard-card-header">
            <div class="dashboard-card-title">
              <span class="dashboard-icon">📈</span>
              <div>
                <h3 class="text-lg font-semibold" style="color: var(--text-primary)">{{ t('financeiro.dashboard.nextMonth') }}</h3>
                <p class="text-xs" style="color: var(--text-secondary)">{{ t('financeiro.dashboard.receivablesForecast') }}</p>
              </div>
            </div>
          </div>
          <div class="dashboard-card-center">
            <MoneyAmount :value="previsao.proximoMes?.totalPrevisto || 0" tag="p" size="lg" class="dashboard-big-value" />
            <p class="text-sm" style="color: var(--text-secondary)">{{ t('financeiro.dashboard.totalEstimate') }}</p>
          </div>
        </div>

        <!-- Card Próximos 12 Meses -->
        <div class="card dashboard-card dashboard-card-tertiary">
          <div class="dashboard-card-header">
            <div class="dashboard-card-title">
              <span class="dashboard-icon">🔮</span>
              <div>
                <h3 class="text-lg font-semibold" style="color: var(--text-primary)">{{ t('financeiro.dashboard.next12Months') }}</h3>
                <p class="text-xs" style="color: var(--text-secondary)">{{ t('financeiro.dashboard.accumulatedForecast') }}</p>
              </div>
            </div>
          </div>
          <div class="dashboard-card-center">
            <MoneyAmount :value="previsao.proximosMeses?.total || 0" tag="p" size="lg" class="dashboard-big-value" />
            <p class="text-sm" style="color: var(--text-secondary)">{{ t('financeiro.dashboard.monthsCount', { count: previsao.proximosMeses?.quantidadeMeses || 12 }) }}</p>
          </div>
        </div>
      </div>

      <!-- Ações Rápidas -->
      <div class="card dashboard-actions">
        <div class="dashboard-actions-header">
          <div class="dashboard-card-title">
            <span class="dashboard-icon">⚡</span>
            <div>
              <h3 class="text-xl font-semibold" style="color: var(--text-primary)">{{ t('financeiro.dashboard.quickActions') }}</h3>
              <p class="text-sm" style="color: var(--text-secondary)">{{ t('financeiro.dashboard.quickActionsSubtitle') }}</p>
            </div>
          </div>
        </div>
        <div class="dashboard-actions-grid">
          <button @click="abrirModalNovoCliente" class="dashboard-action-btn">
            <span class="action-icon">👤</span>
            <div class="action-text">
              <span class="action-title">{{ t('financeiro.dashboard.newClient') }}</span>
              <span class="action-subtitle">{{ t('financeiro.dashboard.registerClient') }}</span>
            </div>
          </button>
          <button @click="abrirModalNovaCobranca" class="dashboard-action-btn action-green">
            <span class="action-icon">💵</span>
            <div class="action-text">
              <span class="action-title">{{ t('financeiro.dashboard.newCharge') }}</span>
              <span class="action-subtitle">{{ t('financeiro.dashboard.generateCharge') }}</span>
            </div>
          </button>
          <button @click="currentView = 'cobrancas'" class="dashboard-action-btn action-orange">
            <span class="action-icon">📋</span>
            <div class="action-text">
              <span class="action-title">{{ t('financeiro.dashboard.viewCharges') }}</span>
              <span class="action-subtitle">{{ t('financeiro.dashboard.manageCharges') }}</span>
            </div>
          </button>
          <button @click="currentView = 'relatorios'" class="dashboard-action-btn action-purple">
            <span class="action-icon">📊</span>
            <div class="action-text">
              <span class="action-title">{{ t('financeiro.dashboard.reports') }}</span>
              <span class="action-subtitle">{{ t('financeiro.dashboard.reportsSubtitle') }}</span>
            </div>
          </button>
        </div>
      </div>
    </div>

    <!-- Clientes View -->
    <ClientesView v-if="currentView === 'clientes'" @atualizar="carregarDados" />

    <!-- Cobranças View -->
    <CobrancasView v-if="currentView === 'cobrancas'" @atualizar="carregarDados" />

    <!-- Contas a Pagar View -->
    <ContasPagarView v-if="currentView === 'contas-pagar'" @atualizar="carregarDados" />

    <!-- Relatórios View -->
    <RelatoriosView v-if="currentView === 'relatorios'" />

    <!-- Configurações View -->
    <ConfiguracoesView v-if="currentView === 'configuracoes'" />
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import api from '../services/api'
import Swal from 'sweetalert2'

const { t } = useI18n()
import ClientesView from '../components/financeiro/ClientesView.vue'
import CobrancasView from '../components/financeiro/CobrancasView.vue'
import ContasPagarView from '../components/financeiro/ContasPagarView.vue'
import RelatoriosView from '../components/financeiro/RelatoriosView.vue'
import ConfiguracoesView from '../components/financeiro/ConfiguracoesView.vue'
import MoneyAmount from '../components/MoneyAmount.vue'

const currentView = ref('dashboard')
const dashboard = ref({})
const previsao = ref({})

onMounted(() => {
  carregarDados()
})

async function carregarDados() {
  try {
    const [dashboardRes, previsaoRes] = await Promise.all([
      api.get('/financeiro/relatorios/dashboard'),
      api.get('/financeiro/relatorios/previsao-recebiveis')
    ])
    dashboard.value = dashboardRes.data
    previsao.value = previsaoRes.data
  } catch (error) {
    console.error('Erro ao carregar dados:', error)
    Swal.fire('Erro', t('financeiro.dashboard.loadError'), 'error')
  }
}

function abrirModalNovoCliente() {
  currentView.value = 'clientes'
  // Trigger evento para abrir modal (implementar no componente)
}

function abrirModalNovaCobranca() {
  currentView.value = 'cobrancas'
  // Trigger evento para abrir modal (implementar no componente)
}
</script>

<style scoped>
.btn-primary {
  @apply px-4 py-2 rounded-lg font-medium transition-all;
  background-color: var(--primary);
  color: white;
}

.btn-primary:hover {
  opacity: 0.9;
}

.btn-menu {
  @apply px-4 py-2 rounded-lg font-medium transition-all;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  border: 1px solid var(--border);
  background-color: var(--bg-secondary);
  color: var(--text-primary);
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.08);
}

.btn-menu-active {
  background-color: var(--info);
  color: #fff;
  border-color: transparent;
  box-shadow: 0 6px 16px rgba(59, 130, 246, 0.35);
}

.btn-menu-inactive {
  border: 1px solid var(--border);
}

.btn-menu-inactive:hover {
  background-color: var(--bg-primary);
  transform: translateY(-1px);
}

.dashboard-card {
  border-radius: 16px;
  border: 1px solid var(--border);
  background: linear-gradient(180deg, var(--bg-secondary), var(--bg-primary));
  box-shadow: 0 10px 20px rgba(15, 23, 42, 0.08);
  padding: 1.25rem 1.5rem;
}

.dashboard-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.75rem;
}

.dashboard-card-title {
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
}

.dashboard-icon {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: rgba(59, 130, 246, 0.18);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
}

.dashboard-card-primary .dashboard-icon {
  background: rgba(59, 130, 246, 0.2);
}

.dashboard-card-secondary .dashboard-icon {
  background: rgba(16, 185, 129, 0.2);
}

.dashboard-card-tertiary .dashboard-icon {
  background: rgba(99, 102, 241, 0.2);
}

.dashboard-card-body {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

.dashboard-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.dashboard-total {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 0.6rem;
  border-top: 1px solid var(--border);
}

.dashboard-total-value {
  font-weight: 700;
  font-size: 1.3rem;
  color: var(--text-primary);
}

.dashboard-card-center {
  text-align: center;
  padding: 1rem 0 0.6rem;
}

.dashboard-big-value {
  font-size: 2.1rem;
  font-weight: 700;
  color: var(--text-primary);
}

.dashboard-actions {
  border-radius: 18px;
  border: 1px solid var(--border);
  background: linear-gradient(180deg, var(--bg-secondary), var(--bg-primary));
  box-shadow: 0 10px 20px rgba(15, 23, 42, 0.08);
  padding: 1.5rem;
}

.dashboard-actions-header {
  margin-bottom: 1rem;
}

.dashboard-actions-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.9rem;
}

.dashboard-action-btn {
  display: flex;
  align-items: center;
  gap: 0.9rem;
  padding: 1rem;
  border-radius: 14px;
  border: 1px solid var(--border);
  background: var(--bg-secondary);
  transition: all 0.2s ease;
  text-align: left;
}

.dashboard-action-btn:hover {
  border-color: rgba(59, 130, 246, 0.4);
  box-shadow: 0 8px 18px rgba(59, 130, 246, 0.15);
  transform: translateY(-1px);
}

.action-icon {
  width: 46px;
  height: 46px;
  border-radius: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  background: rgba(59, 130, 246, 0.15);
}

.action-text {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

.action-title {
  font-weight: 700;
  color: var(--text-primary);
}

.action-subtitle {
  font-size: 0.8rem;
  color: var(--text-secondary);
}

.action-green .action-icon {
  background: rgba(16, 185, 129, 0.18);
}

.action-orange .action-icon {
  background: rgba(249, 115, 22, 0.18);
}

.action-purple .action-icon {
  background: rgba(168, 85, 247, 0.18);
}

@media (min-width: 1024px) {
  .dashboard-actions-grid {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}

.card {
  background-color: var(--bg-secondary);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 1.5rem;
}
</style>
